import { ChangeDetectionStrategy, Component, computed, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Approval, EApprovalStatus } from 'src/app/models/approval.model';
import { ApprovalsMenuItemComponent } from "../approvals-menu-item/approvals-menu-item.component";
import { QueDatePipe } from 'src/app/pipes/date.pipe';
import { ApprovalStatusComponent } from '../approval-status/approval-status.component';
import { Router } from '@angular/router';
import { ApprovalStore } from '../../store/approval-store';
import { ExtraDetailsComponent } from "src/app/features/questionnaires/features/table-grid/features/questionnaires-list-page/components/extra-details/extra-details.component";
import { ManipulationsToolbarStore } from 'src/app/store/manipulations-toolbar-store';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlobalStore } from 'src/app/store/global-store';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ApprovalExpenseRowsComponent } from '../approval-expense-rows/approval-expense-rows.component';
import { PayrollMonthPipe } from 'src/app/pipes/payroll-month.pipe';

@Component({
    selector: 'que-approvals-list-item',
    imports: [CommonModule, ApprovalsMenuItemComponent, QueDatePipe, ApprovalStatusComponent, ExtraDetailsComponent, TranslateDirective, TranslatePipe, ApprovalExpenseRowsComponent, PayrollMonthPipe],
    templateUrl: './approvals-list-item.component.html',
    styleUrls: ['./approvals-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalsListItemComponent {
  @Input() approval?: Approval;
  #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
  #approvalStore = inject(ApprovalStore);
  pendingApprovalTab$ = toSignal(this.#manipulationsToolbarStore.pendingApprovalTab$);
  afterApprovalTab$ = toSignal(this.#manipulationsToolbarStore.afterApprovalTab$);
  downloadRecordsTab$ = toSignal(this.#manipulationsToolbarStore.downloadRecordsTab$);
  selectedFilter$ = toSignal(this.#manipulationsToolbarStore.selectedFilter$);

  // Cached in ApprovalStore, keyed by senderId+filter, so every row belonging to the same sender
  // (there's often more than one) shares a single fetch instead of each row's hover re-fetching it.
  senderApprovedTotals$ = toSignal(this.#approvalStore.senderApprovedTotals$);
  senderApprovedTotal = computed(() => {
    const key = this.senderTotalKey();
    return key ? this.senderApprovedTotals$()?.[key] : undefined;
  });

  expandedIds$ = toSignal(this.#approvalStore.expandedIds$);
  selectedIds$ = toSignal(this.#approvalStore.selectedIds$);
  expenseRowsByApprovalId$ = toSignal(this.#approvalStore.expenseRowsByApprovalId$);
  loadingExpenseRowsIds$ = toSignal(this.#approvalStore.loadingExpenseRowsIds$);
  expenseRowsErrorByApprovalId$ = toSignal(this.#approvalStore.expenseRowsErrorByApprovalId$);

  #router = inject(Router);
  eApprovalStatus = EApprovalStatus

  get isExpanded(): boolean {
    return !!(this.approval && this.expandedIds$()?.[this.approval.id]);
  }
  get isSelected(): boolean {
    return !!(this.approval && this.selectedIds$()?.[this.approval.id]);
  }
  get expenseRows() {
    return this.approval ? this.expenseRowsByApprovalId$()?.[this.approval.id] : undefined;
  }
  get isLoadingExpenseRows(): boolean {
    return !!(this.approval && this.loadingExpenseRowsIds$()?.[this.approval.id]);
  }
  get expenseRowsError(): string | undefined {
    return this.approval ? this.expenseRowsErrorByApprovalId$()?.[this.approval.id] || undefined : undefined;
  }

  toggleExpand() {
    if (this.approval) this.#approvalStore.toggleExpand(this.approval);
  }
  toggleSelect(ev: Event) {
    ev.stopPropagation();
    if (this.approval) this.#approvalStore.toggleSelect(this.approval.id);
  }
  approvalClick() {
    if (this.approval?.recipientGuid) {
      this.#router.navigate(['viewAnswers', this.approval?.recipientGuid, this.approval?.status])
    }
  }
  editGroup() { }

  senderTotalKey = computed(() => {
    const senderId = this.approval?.user?.id;
    if (!senderId) return undefined;
    return `${senderId}:${JSON.stringify(this.selectedFilter$())}`;
  });

  onSenderNameHover() {
    const senderId = this.approval?.user?.id;
    const filterKey = this.senderTotalKey();
    if (!senderId || !filterKey) return;
    this.#approvalStore.fetchSenderApprovedTotal({ senderId, filterKey, filter: this.selectedFilter$() });
  }
}
